import React, { useState } from 'react'
import {
  Box,
  Button,
  Heading,
  Typography,
  Frame,
  TextField,
  Switch,
  List,
  ListItem,
  ListItemText,
  Chip
} from 'components'
import { makeStyles } from '@material-ui/core/styles'
import { ProgressBarFooter } from './FreelancerProgressBarFooter'
import { updateUser, useContent } from 'utils'
import firebase from 'utils/firebase'

const useStyles = makeStyles((theme) => ({
  container: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(3),
  },
  equipmentItem: {
    color: theme.palette.primary.main,
    fontWeight: 500,
  },
  equipmentList: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(1),
  },
  sectionHeading: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
    fontWeight: 500,
  },
  switchContainer: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(3),
  },
  switchLabel: {
    marginLeft: theme.spacing(1),
  }
}))

const FreelancerEquipmentSelect = ({
  onboarding,
  user,
  stepIndex,
  totalSteps,
  onNext = () => { },
  onSubmit = () => { }
}) => {
  const classes = useStyles()
  const [hasEquipment, setHasEquipment] = useState(user?.hasEquipment || false)
  const [equipmentList, setEquipmentList] = useState(user?.equipmentList || [])
  const [currentEquipment, setCurrentEquipment] = useState('')
  const { landing } = useContent() || {}
  const buttonColor = landing?.['onboarding-button-color'] || 'primary'

  const handleToggleEquipment = () => {
    setHasEquipment(!hasEquipment)
    // If toggling from yes to no, clear the equipment list
    if (hasEquipment) {
      setEquipmentList([])
    }
  }

  const handleAddEquipment = () => {
    if (currentEquipment && !equipmentList.includes(currentEquipment)) {
      setEquipmentList([...equipmentList, currentEquipment])
      setCurrentEquipment('')
    }
  }

  const handleRemoveEquipment = (itemToRemove) => {
    setEquipmentList(equipmentList.filter(item => item !== itemToRemove))
  }

  const handleSubmit = () => {
    // Track analytics if in onboarding flow
    if (onboarding) {
      firebase.analytics().logEvent('user_equipment_selected', {
        hasEquipment,
        count: equipmentList.length
      })
    }

    // console.log('Updating user with equipment data:', hasEquipment, equipmentList);

    // Use direct Firebase update with set and merge to ensure data is saved properly
    firebase
      .firestore()
      .collection('users')
      .doc(user.id)
      .set({
        hasEquipment: hasEquipment,
        equipmentList: hasEquipment ? equipmentList : []
      }, { merge: true })
      .then(() => {
        // console.log('Equipment information successfully updated in database')
        if (onNext) {
          onNext()
        }
      })
      .catch(error => {
        console.error('Error updating equipment information:', error)
      })
  }

  return (
    <Frame maxWidth="sm" user={user}>
      <Box mx="auto" maxWidth={530}>
        <Box flex={1} flexVariant="column center" mb={2.5}>
          <Heading>
            Do you have your own gear or software?
          </Heading>
          <Box my={2} />
          <Typography variant="body1">
            Some employers look for people with their own tools of the trade. If that's you, click here.
          </Typography>
        </Box>

        <Box className={classes.container} display="flex" flexDirection="column">
          <Box className={classes.switchContainer}>
            <Typography>No</Typography>
            <Switch
              checked={hasEquipment}
              onChange={handleToggleEquipment}
            />
            <Typography className={classes.switchLabel}>Yes</Typography>
          </Box>

          {hasEquipment && (
            <>
              <Box mb={3}>
                <TextField
                  value={currentEquipment}
                  onChange={(e) => setCurrentEquipment(e.target.value)}
                  placeholder="List each item one at a time"
                  fullWidth
                />
              </Box>

              <Box mb={3}>
                <Button
                  variant="contained"
                  style={{ 'background-color': buttonColor }}
                  onClick={handleAddEquipment}
                  disabled={!currentEquipment}
                  fullWidth
                >
                  Add Gear
                </Button>
              </Box>

              {equipmentList.length > 0 && (
                <Box mt={3}>
                  <Typography variant="h6" className={classes.sectionHeading}>
                    Gear List
                  </Typography>
                  <List className={classes.equipmentList}>
                    {equipmentList.map((item, index) => (
                      <ListItem key={index} dense>
                        <ListItemText
                          primary={item}
                          className={classes.equipmentItem}
                        />
                        <Chip
                          size="small"
                          label="Remove"
                          onClick={() => handleRemoveEquipment(item)}
                          color="secondary"
                          style={{ marginLeft: '8px' }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}
            </>
          )}
        </Box>

        {onboarding && (
          <ProgressBarFooter
            valid={true}
            stepIndex={stepIndex}
            firstStep={stepIndex}
            numSteps={totalSteps}
            onNext={handleSubmit}
            onPrev={() => { }}
          />
        )}

        {!onboarding && (
          <Box my={2} flexVariant="row justify-space-evenly">
            <Button variant="contained" style={{ width: '40%', 'background-color': buttonColor }} onClick={handleSubmit}>Save</Button>
          </Box>
        )}
      </Box>
    </Frame>
  )
}

export default FreelancerEquipmentSelect
